// Loads the portrait once per session and exposes it to Home and Self.
//
// Everything here is eventually consistent by design: the server reflects in the background after a
// push, so a fresh account legitimately has an empty portrait for a while. That is why every field
// is optional and the screens omit cards rather than showing zeros.

import { useCallback, useEffect, useState } from 'react';
import { useSession } from '../auth/SessionProvider';
import {
  fetchArchetypes,
  fetchObservations,
  fetchPatterns,
  type Archetype,
  type EmergingPattern,
  type Observation,
  type Pattern,
  type PortraitSummary,
} from '../api/portrait';

export interface Portrait {
  archetypes: Archetype[];
  patterns: Pattern[];
  /** Noticed but not yet a pattern, with how many sightings are still needed. */
  emerging: EmergingPattern[];
  observations: Observation[];
  summary: PortraitSummary | null;
  depthTier: string | null;
  isLoading: boolean;
  /** The freshest observation, for the Home "Higher Self" card. */
  observation: string | null;
  /** The freshest pattern reading, for the Home "I've noticed…" card. */
  insight: string | null;
  /** The pattern `insight` was written about, so the card can cite it and link to it. */
  insightPattern: Pattern | null;
  reload(): void;
}

export function usePortrait(): Portrait {
  const { vault } = useSession();
  const dek = vault.status === 'unlocked' ? vault.dek : null;

  const [archetypes, setArchetypes] = useState<Archetype[]>([]);
  const [patterns, setPatterns] = useState<Pattern[]>([]);
  const [emerging, setEmerging] = useState<EmergingPattern[]>([]);
  const [observations, setObservations] = useState<Observation[]>([]);
  const [summary, setSummary] = useState<PortraitSummary | null>(null);
  const [depthTier, setDepthTier] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!dek) return;
    const controller = new AbortController();

    (async () => {
      // Independent reads, so fetch together and let each fail on its own. An empty portrait is a
      // normal state for a new account, never an error worth interrupting the user about.
      const [archetypeResult, patternResult, observationResult] = await Promise.allSettled([
        fetchArchetypes(dek, controller.signal),
        fetchPatterns(dek, controller.signal),
        fetchObservations(dek, controller.signal),
      ]);

      if (controller.signal.aborted) return;

      if (archetypeResult.status === 'fulfilled') {
        setArchetypes(archetypeResult.value.archetypes);
        setSummary(archetypeResult.value.summary);
        setDepthTier(archetypeResult.value.depthTier);
      }
      if (patternResult.status === 'fulfilled') {
        setPatterns(patternResult.value.patterns);
        setEmerging(patternResult.value.emerging);
      }
      if (observationResult.status === 'fulfilled') setObservations(observationResult.value);

      setIsLoading(false);
    })();

    return () => controller.abort();
  }, [dek, nonce]);

  const freshest = observations.find((o) => o.fresh) ?? observations[0];
  const strongestPattern = patterns.find((p) => p.meaning.trim().length > 0);

  return {
    archetypes,
    patterns,
    emerging,
    observations,
    summary,
    depthTier,
    isLoading,
    observation: freshest?.text ?? null,
    insight: strongestPattern?.meaning ?? null,
    /** The pattern the insight was written about — its symbol, its count, and where it leads. */
    insightPattern: strongestPattern ?? null,
    reload,
  };
}
